import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Inbox, Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle2, Sparkles, Eye, AlertCircle, RefreshCw, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { API } from '../utils/api';
import { authAxios } from '../utils/authAxios';

// Intervalo de polling en milisegundos
var POLL_INTERVAL_MS = 30000;
// Sonido de notificación (reutiliza el usado en la experiencia)
var NOTIFY_SOUND_URL = 'https://cdn.pixabay.com/audio/2022/03/10/audio_27be5f2d9e.mp3';

/**
 * Inbox de cotizaciones recibidas para centros y profesionales.
 * @param {string} role - 'center' | 'professional'
 */
export default function QuoteRequestsInbox({ role = 'center' }) {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [filter, setFilter] = useState('all'); // all | unread | contacted
  var [expanded, setExpanded] = useState(null);
  var [refreshing, setRefreshing] = useState(false);
  var [lastCheck, setLastCheck] = useState(null);
  var knownIdsRef = useRef(new Set());
  var audioRef = useRef(null);
  var soundEnabledRef = useRef(true);
  var isFirstLoadRef = useRef(true);

  // Inicializar audio y leer preferencia de sonido desde localStorage
  useEffect(function() {
    try {
      audioRef.current = new Audio(NOTIFY_SOUND_URL);
      audioRef.current.volume = 0.4;
      audioRef.current.preload = 'auto';
    } catch (e) {}
    try {
      var pref = localStorage.getItem('quote_inbox_sound');
      if (pref === '0') soundEnabledRef.current = false;
    } catch (e) {}
  }, []);

  // Primera carga + polling cada 30s (solo si la pestaña está visible)
  useEffect(function() {
    load(true);
    var poll = setInterval(function() {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        load(false);
      }
    }, POLL_INTERVAL_MS);

    // Cuando el usuario regresa a la pestaña, refrescar de inmediato
    function onVisibility() {
      if (document.visibilityState === 'visible') load(false);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return function() {
      clearInterval(poll);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function load(initial) {
    if (initial) setLoading(true); else setRefreshing(true);
    setError(null);
    try {
      var path = role === 'professional' ? '/professional/quote-requests/my' : '/center/quote-requests/my';
      var res = await authAxios.get(API + path);
      var list = res.data.quote_requests || [];
      // Detectar nuevas cotizaciones comparando con IDs conocidos
      var currentIds = new Set(list.map(function(q) { return q.quote_id; }));
      var newOnes = [];
      if (!isFirstLoadRef.current) {
        list.forEach(function(q) {
          if (q.quote_id && !knownIdsRef.current.has(q.quote_id)) newOnes.push(q);
        });
      }
      knownIdsRef.current = currentIds;
      setItems(list);
      setLastCheck(new Date());
      if (newOnes.length > 0) {
        notifyNew(newOnes);
      }
      isFirstLoadRef.current = false;
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando cotizaciones');
    }
    if (initial) setLoading(false); else setRefreshing(false);
  }

  function notifyNew(newOnes) {
    var count = newOnes.length;
    var first = newOnes[0] || {};
    // Sonido
    if (soundEnabledRef.current && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(function() {});
      } catch (e) {}
    }
    // Toast
    var label = count === 1
      ? 'Nueva cotización de ' + (first.name || 'un paciente')
      : count + ' nuevas cotizaciones';
    try {
      toast.success(label, {
        description: first.region ? 'Región: ' + first.region + (first.urgency === 'inmediata' ? ' · Urgente' : '') : undefined,
        duration: 6000,
        action: {
          label: 'Ver',
          onClick: function() {
            setFilter('unread');
            if (newOnes[0] && newOnes[0].quote_id) setExpanded(newOnes[0].quote_id);
          }
        }
      });
    } catch (e) {}
    // Notificación nativa del navegador (si tiene permiso)
    try {
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        new Notification(label, {
          body: first.region ? 'Región: ' + first.region : 'Responde rápido por WhatsApp para ganar al paciente.',
          icon: '/favicon.ico',
          tag: 'quote-' + (first.quote_id || 'new')
        });
      }
    } catch (e) {}
  }

  function toggleSound() {
    soundEnabledRef.current = !soundEnabledRef.current;
    try { localStorage.setItem('quote_inbox_sound', soundEnabledRef.current ? '1' : '0'); } catch (e) {}
    // Forzar re-render
    setLastCheck(new Date());
    // Pedir permiso de notificación si está activando sonido
    try {
      if (soundEnabledRef.current && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(function() {});
      }
    } catch (e) {}
  }

  async function markRead(quote_id) {
    try {
      await authAxios.post(API + '/quote-requests/' + quote_id + '/mark-read');
      setItems(function(prev) { return prev.map(function(q) { return q.quote_id === quote_id ? Object.assign({}, q, { was_read: true }) : q; }); });
    } catch (e) {}
  }

  async function markContacted(quote_id) {
    try {
      await authAxios.post(API + '/quote-requests/' + quote_id + '/mark-contacted');
      setItems(function(prev) { return prev.map(function(q) { return q.quote_id === quote_id ? Object.assign({}, q, { was_contacted: true, was_read: true }) : q; }); });
    } catch (e) {}
  }

  function toggleExpand(q) {
    var isExp = expanded === q.quote_id;
    setExpanded(isExp ? null : q.quote_id);
    if (!isExp && !q.was_read) markRead(q.quote_id);
  }

  var filtered = items.filter(function(q) {
    if (filter === 'unread') return !q.was_read;
    if (filter === 'contacted') return q.was_contacted;
    return true;
  });

  var unreadCount = items.filter(function(q) { return !q.was_read; }).length;
  var contactedCount = items.filter(function(q) { return q.was_contacted; }).length;

  function formatDate(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      var now = new Date();
      var diffHours = (now - d) / (1000 * 60 * 60);
      if (diffHours < 1) return 'Hace ' + Math.round(diffHours * 60) + ' min';
      if (diffHours < 24) return 'Hace ' + Math.round(diffHours) + ' h';
      return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return iso; }
  }

  function urgencyBadge(u) {
    if (!u) return null;
    var map = {
      inmediata: { label: 'Urgente', cls: 'bg-red-500/20 text-red-400 border-red-500/40' },
      esta_semana: { label: 'Esta semana', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
      flexible: { label: 'Flexible', cls: 'bg-slate-500/20 text-slate-400 border-slate-500/40' }
    };
    var info = map[u] || { label: u, cls: 'bg-slate-500/20 text-slate-400 border-slate-500/40' };
    return <span className={'px-2 py-0.5 text-xs rounded-full border ' + info.cls}>{info.label}</span>;
  }

  function whatsappLink(phone) {
    var clean = (phone || '').replace(/[^0-9]/g, '');
    return 'https://wa.me/' + clean;
  }

  return (
    <div className="space-y-4" data-testid="quote-inbox">
      {/* Header with stats + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Inbox className="w-5 h-5 text-emerald-400" />
          <h2 className="text-xl font-bold text-slate-50">Cotizaciones recibidas</h2>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full" data-testid="unread-badge">{unreadCount}</span>
          )}
          <button
            onClick={function() { load(false); }}
            disabled={refreshing}
            data-testid="quote-refresh-btn"
            title={lastCheck ? 'Última actualización: ' + lastCheck.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Refrescar'}
            className="ml-1 p-1.5 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition disabled:opacity-50"
            aria-label="Refrescar cotizaciones"
          >
            <RefreshCw className={'w-4 h-4 ' + (refreshing ? 'animate-spin text-emerald-400' : '')} />
          </button>
          <button
            onClick={toggleSound}
            data-testid="quote-sound-toggle"
            title={soundEnabledRef.current ? 'Desactivar avisos sonoros' : 'Activar avisos sonoros'}
            className={'p-1.5 rounded-lg transition ' + (soundEnabledRef.current ? 'text-emerald-400 hover:bg-slate-800' : 'text-slate-600 hover:text-slate-400 hover:bg-slate-800')}
            aria-label="Avisos sonoros"
          >
            <Bell className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 text-xs">
          <button onClick={function() { setFilter('all'); }} data-testid="filter-all" className={'px-3 py-1.5 rounded-lg transition ' + (filter === 'all' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 hover:text-slate-200')}>
            Todas ({items.length})
          </button>
          <button onClick={function() { setFilter('unread'); }} data-testid="filter-unread" className={'px-3 py-1.5 rounded-lg transition ' + (filter === 'unread' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 hover:text-slate-200')}>
            No leídas ({unreadCount})
          </button>
          <button onClick={function() { setFilter('contacted'); }} data-testid="filter-contacted" className={'px-3 py-1.5 rounded-lg transition ' + (filter === 'contacted' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 hover:text-slate-200')}>
            Contactadas ({contactedCount})
          </button>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm text-blue-200 flex items-start gap-3">
        <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          Las personas que buscan ayuda envían su cotización a varios {role === 'professional' ? 'profesionales' : 'centros'} a la vez.
          <strong className="text-blue-100"> Responde rápido</strong> para ganar al paciente — las primeras respuestas por WhatsApp suelen cerrar.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      ) : error ? (
        <Card className="bg-red-500/10 border-red-500/30"><CardContent className="p-6"><p className="text-red-300 text-sm">{error}</p></CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-10 text-center">
            <Inbox className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400">
              {filter === 'unread' ? 'No tienes cotizaciones sin leer.' : filter === 'contacted' ? 'Aún no has marcado cotizaciones como contactadas.' : 'Aún no has recibido cotizaciones. Cuando alguien solicite una cotización desde el sitio, te llegarán aquí y a tu email.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(function(q) {
            var isExp = expanded === q.quote_id;
            return (
              <Card
                key={q.quote_id}
                data-testid={'quote-card-' + q.quote_id}
                className={'cursor-pointer transition-all ' + (q.was_read ? 'bg-slate-900/50 border-slate-800' : 'bg-slate-900 border-emerald-500/40 shadow-lg shadow-emerald-500/5')}
                onClick={function() { toggleExpand(q); }}
              >
                <CardContent className="p-4 md:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {!q.was_read && <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" aria-label="No leído"></span>}
                        <h3 className="font-semibold text-slate-50 text-base">{q.name}</h3>
                        {urgencyBadge(q.urgency)}
                        {q.was_contacted && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/40 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />Contactado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-sm text-slate-400">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{q.region || 'Región no especificada'}</span>
                        {q.modality && <span className="capitalize">{q.modality}</span>}
                        {q.addiction && <span className="text-emerald-400">· {q.addiction}</span>}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3" />
                      {formatDate(q.created_at)}
                    </div>
                  </div>

                  {isExp && (
                    <div className="mt-4 pt-4 border-t border-slate-800 space-y-4" data-testid="quote-details">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="bg-slate-800/40 rounded-lg p-3">
                          <p className="text-slate-500 text-xs mb-1">Teléfono / WhatsApp</p>
                          <p className="text-slate-200 font-medium">{q.phone}</p>
                        </div>
                        {q.email && (
                          <div className="bg-slate-800/40 rounded-lg p-3">
                            <p className="text-slate-500 text-xs mb-1">Email</p>
                            <p className="text-slate-200 font-medium break-all">{q.email}</p>
                          </div>
                        )}
                      </div>

                      {q.message && (
                        <div className="bg-slate-800/40 rounded-lg p-3 border-l-2 border-emerald-500">
                          <p className="text-slate-500 text-xs mb-1">Mensaje del paciente</p>
                          <p className="text-slate-300 text-sm italic">"{q.message}"</p>
                        </div>
                      )}

                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-200">
                          Esta cotización fue enviada a <strong>{q.matched_count}</strong> {role === 'professional' ? 'profesionales' : 'centros'} de la misma región. Responde rápido.
                        </p>
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <a
                          href={whatsappLink(q.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={function(e) { e.stopPropagation(); markContacted(q.quote_id); }}
                          data-testid={'quote-whatsapp-' + q.quote_id}
                          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition"
                        >
                          <MessageCircle className="w-4 h-4" />WhatsApp
                        </a>
                        <a
                          href={'tel:' + q.phone}
                          onClick={function(e) { e.stopPropagation(); markContacted(q.quote_id); }}
                          className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-lg font-medium text-sm transition"
                        >
                          <Phone className="w-4 h-4" />Llamar
                        </a>
                        {q.email && (
                          <a
                            href={'mailto:' + q.email}
                            onClick={function(e) { e.stopPropagation(); markContacted(q.quote_id); }}
                            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-lg font-medium text-sm transition"
                          >
                            <Mail className="w-4 h-4" />Email
                          </a>
                        )}
                        {!q.was_contacted && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={function(e) { e.stopPropagation(); markContacted(q.quote_id); }}
                            data-testid={'quote-mark-contacted-' + q.quote_id}
                            className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />Marcar contactado
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isExp && (
                    <button className="mt-3 text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                      <Eye className="w-3 h-3" />Ver datos de contacto
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
